import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import PageTitle from '../components/PageTitle';
import { useCart } from '../context/CartContext';
import { formatCurrencyShort } from '../utils/currency';
import { rooms } from '../data/rooms';
import { BRAND, whatsappLink } from '../data/brand';

function RoomCard({ room, onSelect }) {
  const { addToCart, cartItems } = useCart();
  const [flash, setFlash] = useState(false);
  const inCart = cartItems.some(i => i.id === room.id);
  const handleAdd = () => { addToCart({ ...room, currency:'₦' }); setFlash(true); setTimeout(() => setFlash(false), 1800); };

  return (
    <div className="page-room-card">
      <div className="page-room-img">
        <img src={room.img} alt={room.title} />
        <span className="page-room-tag" style={{ background: room.color }}>{room.tag}</span>
        <span className="page-room-price">₦{formatCurrencyShort(room.price).replace("₦","")}<small>/night</small></span>
      </div>
      <div className="page-room-body">
        <h3>{room.title}</h3>
        <p>{room.desc}</p>
        <ul className="page-room-feats">
          {room.features.map((f,i) => <li key={i}><i className="fas fa-check"></i>{f}</li>)}
        </ul>
        <div className="page-room-meta">
          <span><i className="fas fa-user"></i>{room.persons}</span>
          <span><i className="fas fa-ruler-combined"></i>{room.size}</span>
        </div>
        <div className="page-room-actions">
          <button className="page-room-detail-btn" onClick={() => onSelect(room)}>Details</button>
          <button className={"h2-book-btn" + (inCart?' incart':'') + (flash?' added':'')} onClick={handleAdd}>
            {flash ? '✓ Added' : inCart ? '✓ In Cart' : 'Book'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoomModal({ room, onClose }) {
  const { addToCart, cartItems } = useCart();
  const [flash, setFlash] = useState(false);
  const inCart = cartItems.some(i => i.id === room.id);
  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);
  const handleAdd = () => { addToCart({ ...room, currency:'₦' }); setFlash(true); setTimeout(() => setFlash(false), 1800); };
  return (
    <div className="gw-modal-overlay" onClick={onClose}>
      <div className="gw-modal" onClick={e => e.stopPropagation()}>
        <button className="gw-modal-close" onClick={onClose}><i className="fas fa-times"></i></button>
        <div className="gw-modal-img"><img src={room.img} alt={room.title} /><span className="gw-room-tag" style={{ background: room.color }}>{room.tag}</span></div>
        <div className="gw-modal-body">
          <div className="h2-label">{room.tag} Room</div>
          <h2 style={{ fontFamily:'var(--gw-font-h,serif)', fontSize:'clamp(22px,3vw,38px)', margin:'6px 0 8px', color:'#111' }}>{room.title}</h2>
          <div className="gw-modal-price">₦{formatCurrencyShort(room.price).replace("₦","")}<span>/night</span></div>
          <p style={{ color:'#555', lineHeight:1.8, marginBottom:18 }}>{room.desc}</p>
          <div className="gw-modal-meta">
            <div><i className="fas fa-user" style={{color:'#b08d57'}}></i><strong>Guests</strong><span>{room.persons}</span></div>
            <div><i className="fas fa-ruler-combined" style={{color:'#b08d57'}}></i><strong>Size</strong><span>{room.size}</span></div>
          </div>
          <ul className="gw-modal-features">
            {room.features.map((f,i) => <li key={i}><i className="fas fa-circle-check"></i>{f}</li>)}
          </ul>
          <div className="gw-modal-actions">
            <button className={"gw-btn-book"+(inCart?' in-cart':'')+(flash?' just-added':'')} onClick={handleAdd} style={{flex:1}}>
              {flash?'✓ Added!':inCart?'✓ In Cart':'Book This Room'}
            </button>
            <Link to="/contact" className="gw-btn-contact" onClick={onClose}><i className="fas fa-phone me-1"></i>Call to Reserve</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Rooms() {
  const { cartCount } = useCart();
  const [selected, setSelected] = useState(null);

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle title="Rooms & Tariff" breadcrumbs={[{ label:'Rooms' }]} />

      <section style={{ padding:'60px 0' }}>
        <div className="auto-container">

          {/* Compact tariff table — all 7 categories */}
          <div className="page-tariff-table">
            <div className="page-tariff-head">
              <img src={BRAND.logo} alt={BRAND.name} style={{ height:48, objectFit:'contain' }} />
              <div>
                <strong>Official Room Tariff</strong>
                <span>{BRAND.addressLines.join(', ')}</span>
              </div>
            </div>
            <div className="page-tariff-row" style={{ gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {rooms.map((t,i) => (
                <div key={i} className="page-tariff-cell" style={{ borderTop:`3px solid ${t.color}` }}>
                  <span className="ptc-tag" style={{ color:t.color }}>{t.title}</span>
                  <span className="ptc-price">₦{t.price.toLocaleString()}</span>
                  <span className="ptc-per">per night</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cart notice */}
          {cartCount > 0 && (
            <div className="cart-notice mb-4">
              <i className="fas fa-cart-shopping"></i>
              <span><strong>{cartCount}</strong> item{cartCount>1?'s':''} in cart</span>
              <Link to="/cart" className="cart-notice-link">View Cart →</Link>
            </div>
          )}

          {/* Room cards */}
          <div className="page-rooms-grid">
            {rooms.map(r => <RoomCard key={r.id} room={r} onSelect={setSelected} />)}
          </div>

          {/* CTA */}
          <div className="page-rooms-cta">
            <div>
              <strong>Ready to book? Call us directly</strong>
              <span>Our team is available 24 hours a day</span>
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <a href={`tel:${BRAND.phones[0].tel}`} className="gw-cta-phone"><i className="fas fa-phone"></i>{BRAND.phones[0].label}</a>
              <a href={whatsappLink()} className="gw-cta-phone gw-cta-whatsapp"><i className="fab fa-whatsapp"></i>WhatsApp</a>
            </div>
            <Link to="/contact" className="h2-btn-primary">Book Online</Link>
          </div>
        </div>
      </section>

      {selected && <RoomModal room={selected} onClose={() => setSelected(null)} />}
      <Footer />
      <ScrollToTop />
    </div>
  );
}
